/**
 * API Configuration
 * 
 * Central configuration for API endpoints to ensure consistency
 * throughout the application.
 */

// Import company-specific configurations
import companyApiConfig from './companyApiConfig';

// Set this to true to force the application to use the Cloudflare Workers endpoints
const FORCE_CLOUDFLARE_MODE = false;

// Determine if we're in electron mode
const isElectronMode = () => {
  return window.electronAPI !== undefined;
};

// Determine if we're in browser mode
const isBrowserMode = () => {
  return !isElectronMode();
};

// Determine which API base to use
const getApiBase = () => {
  // In Electron mode with HTTP backend, use localhost:5001
  if (isElectronMode()) {
    // Check if we have IPC handlers (old standalone mode) or HTTP mode (new SQLite mode)
    const hasIPC = window.electronAPI && typeof window.electronAPI.get === 'function';
    
    if (!hasIPC) {
      // Use HTTP backend (SQLite)
      console.log('🔌 Electron mode: Using HTTP backend at localhost:5001');
      return 'http://localhost:5001/api';
    } else {
      // Use IPC (JSON files - legacy)
      console.log('🔌 Electron mode: Using IPC (JSON files)');
      return null; // No API base needed for Electron IPC
    }
  }
  
  // Production build (Railway / custom API host) — must win over legacy Pages→Worker map
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // If we're on the Cloudflare Pages domain, use company-specific Worker URL
  const hostname = window.location.hostname;
  const isCloudflarePages =
    hostname.includes('pages.dev') || hostname.endsWith('qawaemai.com');
  if (FORCE_CLOUDFLARE_MODE || isCloudflarePages) {
    const companyApiBackend = companyApiConfig.getCompanyApiBackend();
    if (companyApiBackend) {
      return companyApiBackend;
    }
    // Fallback to default API if no company-specific API is found
    return 'https://qawaem-platform.qawaemaicode.workers.dev/functions';
  }
  
  // Local browser dev — call Express API directly (CORS enabled on :5001).
  // CRA setupProxy can hang under heavy compiles; direct URL is more reliable than /api proxy.
  if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    return process.env.REACT_APP_DEV_API_URL || 'http://localhost:5001/api';
  }
  
  // Fallback to the default Cloudflare Functions path in production browser mode
  return '/functions';
};

// Export the API base URL and detection functions
export const API_BASE = getApiBase();
export const isElectron = isElectronMode;
export const isBrowser = isBrowserMode;

// Log the API configuration
export const logApiConfig = () => {
  if (isElectronMode()) {
    console.log('🔌 Using Electron IPC for data access (no HTTP API)');
  } else {
    console.log(`🔌 API configured with base URL: ${API_BASE}`);
  }
};

export default {
  API_BASE,
  isElectron,
  isBrowser,
  logApiConfig
};
